"use client"

import styled from "@emotion/styled"
import { GetComponentQuery, GetPageByRouteQuery } from "@eurodrive/apollo"
import { ComparisonBox, FormBlock, PreviouslyViewed } from "@eurodrive/ui"
import {
  CarPageInfo,
  extractProperty,
  getBrands,
  usePreviouslyViewed,
} from "@eurodrive/utils"
import { SaleCarDto } from "api"
import { useEffect, useRef } from "react"
import AccordionComponent from "./Accordion"
import Breadcrumbs from "./Breadcrumbs"
import ColorPicker from "./ColorPicker"
import Hero from "./Hero"
import InteriorCarousel from "./InteriorCarousel"
import Specs from "./Specs"
import StickyHeader from "./StickyHeader"
import TextImageBlock from "./TextImageBlock"
import { CarUmbraco } from "./extractUmbraco"

const StyledInnerContainer = styled.div`
  margin-top: 180px;
  width: 100%;
  @media (min-width: 410px) {
    margin-top: 170px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: -28px;
  }
`

export default function CarPage({
  data,
  brands,
  carUmbraco,
  contactForm,
  carPageInfo,
  comparisonTexts,
}: {
  data: SaleCarDto
  brands: GetComponentQuery
  carUmbraco?: CarUmbraco
  contactForm: GetPageByRouteQuery
  carPageInfo: CarPageInfo
  comparisonTexts: any
}) {
  const twoCarsText = extractProperty(comparisonTexts?.[0], "twoCarsText")
  const oneCarText = extractProperty(comparisonTexts?.[0], "oneCarText")
  const formRef = useRef<HTMLDivElement>(null)
  const mainImage = data.detailedImages?.images?.find(
    (el) => el.label === "SRP",
  )
  const secondHand = data.carClassification === "UsedCar"
  const brandsUmbraco = getBrands({ brands })
  const { addViewedCar } = usePreviouslyViewed<"Sale">({
    brands: brandsUmbraco,
  })
  const contentForm = carUmbraco?.contentForm?.[0]

  const scrollToForm = () => {
    if (formRef.current) {
      const elementPosition = formRef.current.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - 150

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      })

      // Focus on the first input field after scrolling
      setTimeout(() => {
        const firstInput = formRef.current?.querySelector(
          'input:not([type="hidden"]):not([disabled])',
        ) as HTMLInputElement | null
        firstInput?.focus()
      }, 500)
    }
  }

  useEffect(() => {
    addViewedCar(data, "Sale")
  }, [])
  if (!data.carZone) return null
  return (
    <>
      <Hero mainImage={mainImage?.url || ""} />
      <StickyHeader
        carName={data.modelName}
        price={Number(data.advertisedPrice) || Number(data.discountedPrice)}
        secondHand={secondHand}
        color={data.colorDescription}
        description={data.types[0]?.vehicleTypeForWebsiteDescription}
        disclaimer={carPageInfo.legallyForComparison}
        onTestDriveClick={scrollToForm}
        carData={data}
      />
      <StyledInnerContainer>
        <Breadcrumbs carName={data.modelName} />
        <Specs data={data} carUmbraco={carUmbraco} secondHand={secondHand} />
        {!secondHand && (
          <TextImageBlock
            data={data}
            carUmbraco={carUmbraco}
            aboutText={carPageInfo.aboutTitle}
            zeroKmTitle={carPageInfo.vehicleInnerAboutTitle}
            zeroKmContent={carPageInfo.vehicleInnerAboutContent}
          />
        )}
        {carUmbraco?.colors && !secondHand ? (
          <ColorPicker colors={carUmbraco.colors} />
        ) : null}

        <AccordionComponent
          showSpinCar={secondHand}
          data={data.carZone.spec}
          detailedImages={data.detailedImages}
          vehicleInfoBlockMapped={carPageInfo.vehicleInfoBlockMapped}
        />

        <InteriorCarousel
          pictures={data.detailedImages?.images}
          title={`${carPageInfo.vehicleGalleryTitle} ${data.modelName}`}
          vehicleId={`sale-${data.vehicleNumber}`}
        />
        <PreviouslyViewed
          comparisonBlock={comparisonTexts?.[0]}
          brands={brandsUmbraco}
          title={carPageInfo.vehicleLastSeenCarTitle}
        />

        <div ref={formRef} style={{ display: "flex" }}>
          <FormBlock
            contactForm={contactForm}
            carLeadType="sale"
            modelName={data.modelName}
          />
        </div>
      </StyledInnerContainer>

      <ComparisonBox comparisonBlock={comparisonTexts?.[0]} />
    </>
  )
}
